import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { startPayment } from "@/lib/payment";
import { ActivityPicker, activitiesPayload, activitiesTotal } from "@/components/ActivityPicker";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export default function ActivitiesDialog({ open, onOpenChange, onPaid }) {
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected({}); setForm({ name: "", mobile: "", email: "" }); setLoading(false);
      api.get("/activities").then((r) => setActivities(r.data)).catch(() => {});
    }
  }, [open]);

  const total = activitiesTotal(activities, selected);
  const items = activitiesPayload(activities, selected);
  const valid = form.name && form.mobile && form.email && items.length > 0;

  const submit = () => {
    if (!valid) { toast.error("Enter your details and select at least one activity ticket."); return; }
    const payload = { pass_type: "activity", pass_type_key: "activity", ...form, activities: items };
    onOpenChange(false);
    startPayment({ payload, setLoading, onSuccess: (_id, amount) => onPaid && onPaid("Activity Tickets", amount, form.name) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="activities-dialog" className="bg-[#121212] border-border max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-800 uppercase tracking-tight flex items-center gap-2">
            <Ticket className="h-6 w-6 text-[#FF4500]" /> Activity Tickets
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <h3 className="font-display text-sm font-700 uppercase tracking-widest text-[#FF7043] mb-2.5">Your details</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input data-testid="activities-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name *" />
              <Input data-testid="activities-mobile" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="Mobile *" />
              <Input data-testid="activities-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email *" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-sm font-700 uppercase tracking-widest text-[#FF7043] mb-2.5">Choose activities</h3>
            {activities.length === 0 ? (
              <p className="text-sm text-[#71717A]" data-testid="activities-empty">No activities available right now.</p>
            ) : (
              <ActivityPicker activities={activities} selected={selected} setSelected={setSelected} testPrefix="activities-item" />
            )}
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-white/[0.02] p-4">
            <span className="font-display uppercase tracking-wide text-[#FF7043] font-700">Total</span>
            <span data-testid="activities-total" className="font-display text-2xl font-900 text-white">{inr(total)}</span>
          </div>

          <Button data-testid="activities-pay-button" onClick={submit} disabled={loading || !valid} className="w-full h-14 rounded-full bg-[#FF4500] hover:bg-[#FF5722] text-white text-lg font-bold uppercase tracking-wide transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Pay ${inr(total)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
